import * as React from "react";
import { graphql } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import { Media } from "../components/Media";
import Seo from "../components/Seo";
import AussieOwned from "../components/seals/AustralianOwned";
import Vegan from "../components/seals/VeganFriendly";
import GlutenFree from "../components/seals/GlutenFree";
import SugarFree from "../components/seals/SugarFree";
import StickyWrapper from "../components/StickyWrapper";
import RockyTransition from "../components/bActive/RockyTransition";
import Magnesium from "../images/svgs/magnesium.svg";
import ogImage from "../images/loveYourMuscles/love-your-muscles-cyclist.jpg";
import "../styles/loveYourMuscles.css";

const LoveYourMuscles = ({ data }) => {
  const product = getImage(data.product);
  const products = getImage(data.products);
  const cyclist = getImage(data.cyclist);
  const curl = getImage(data.curl);

  return (
    <>
      <Seo
        title="Love Your Muscles™: High Strength Magnesium Powder"
        description="Your muscles need more than just protein to thrive. Love Your Muscles™ is an easily absorbable Magnesium power to assist with muscle performance and recovery."
        image={`https://www.ithrivenutrition.com.au${ogImage}`}
        alt="Love Your Muscles bottle in front of a cyclist pedaling uphill."
        handle="love-your-muscles"
      />
      <div className="bg-gray-darker">
        <div className="max-w-screen-lg m-auto pt-2 md:pt-6 lg:pt-10">
          <StickyWrapper
            title="Love Your Muscles"
            subtitle="High Strength Magnesium Powder For Optimal Muscle Function"
            shopLink="love-your-muscles"
          />
          <div className="flex flex-wrap-reverse md:flex-nowrap">
            <div className="text-white mt-0 md:mt-10 w-full px-6 md:p-0 md:w-5/12">
              <p>
                <strong>Love Your Muscles™</strong> is an easily absorbable
                Magnesium powder formulated to assist with:
              </p>
              <ul className="list-inside mt-2">
                <li className="list-circle white">
                  Muscle performance and function
                </li>
                <li className="list-circle white">Muscle cramp relief*</li>
                <li className="list-circle white">Nervous system support</li>
                <li className="list-circle white">Energy production</li>
                <li className="list-circle white">
                  Exercise performance and recovery
                </li>
                <li className="list-circle white">Cardiovascular response</li>
              </ul>

              <div className="flex justify-center my-10">
                <AussieOwned fill="#2BAE76" />
                <Vegan fill="#2BAE76" />
                <GlutenFree fill="#2BAE76" />
                <SugarFree fill="#2BAE76" />
              </div>
            </div>
            <div className="w-full text-right -mb-8 md:mb-0 md:w-7/12 relative">
              <Media lessThan="md">
                <GatsbyImage
                  image={product}
                  alt="Love Your Muscles bottle"
                  className="bottle-hero"
                />
              </Media>
              <Media greaterThanOrEqual="md">
                <GatsbyImage
                  image={products}
                  alt="Love Your Muscles bottle"
                  className="bottles-hero"
                  style={{ position: "absolute", width: "300px" }}
                />
              </Media>
            </div>
          </div>
        </div>
      </div>
      <div className="green-gradient"></div>
      <div className="relative overflow-hidden">
        <div className="loveYourMusclesHero md:h-full md:w-full 2xl:mx-auto md:relative md:-mb-6">
          <BgImage
            image={cyclist}
            style={{ height: "100%", backgroundPosition: "0% 55%" }}
          />
          <Media greaterThanOrEqual="md">
            <div className="absolute right-0 top-0 bottom-0 flex items-center 2xl:left-1/2">
              <div className="bg-white bg-opacity-80 py-12 md:px-12 xl:px-24">
                <h2 className="text-40 text-center mb-4 text-blue-dark">
                  Get More From
                  <br />
                  <span className="text-green text-50">Your Muscles.</span>
                </h2>
                <p className="max-w-prose mx-auto leading-loose relative mb-3">
                  Your muscles carry you through everything from the everyday to
                  the extraordinary. Help them perform at their best with our
                  high strength magnesium formula.
                </p>
              </div>
            </div>
          </Media>
        </div>
        <RockyTransition />
        <Media lessThan="md" className="md:mb-12">
          <div className="bg-white p-8 pb-20">
            <h2 className="text-30 text-center mb-4 relative text-blue-dark leading-relaxed">
              Get More From
              <br />
              <span className="text-green text-40">Your Muscles.</span>
            </h2>
            <p className="max-w-prose mx-auto leading-loose relative mb-3">
              Your muscles carry you through everything from the everyday to the
              extraordinary. Help them perform at their best with our high
              strength magnesium formula.
            </p>
          </div>
        </Media>
        <RockyTransition green={true} height="70" />
        <div className="grid grid-cols-1 md:grid-cols-2 md:shadow-md magnesiumGradient">
          <div className="text-white self-center 2xl:justify-self-end 2xl:mr-32">
            <div className="relative flex items-center justify-center mb-6 px-6 md:px-0">
              <Magnesium className="inline-block w-24 md:w-28" />
              <h2 className="text-30 ml-4 md:ml-6 inline-block md:text-45 leading-normal relative">
                Why
                <br />
                Magnesium?
              </h2>
            </div>
            <p className="px-8 pb-4 leading-loose max-w-md mx-auto md:text-18 md:leading-loose">
              Magnesium is involved in over 300 biochemical processes in your
              body including:
            </p>
            <ul className="px-8 max-w-md mx-auto md:pl-12">
              <li className="list-circle white">
                Muscle performance and function
              </li>
              <li className="list-circle white">Muscle cramp relief*</li>
              <li className="list-circle white">Nervous system support</li>
              <li className="list-circle white">Energy production</li>
              <li className="list-circle white">
                Exercise performance and recovery
              </li>
              <li className="list-circle white">Cardiovascular response</li>
            </ul>
          </div>
          <GatsbyImage
            image={curl}
            alt="Woman curling dumbell"
            className="ml-8 xl:max-w-2xl"
          />
        </div>
      </div>
      <div className="bg-gray-dark">
        <p className="px-8 lg:px-0 py-2 mb-2 text-gray-lighter max-w-screen-lg mx-auto">
          * When dietary intake is inadequate
        </p>
      </div>
    </>
  );
};

export default LoveYourMuscles;

export const query = graphql`
  query {
    product: file(
      relativePath: { eq: "loveYourMuscles/love-your-muscles-mobile.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    products: file(
      relativePath: { eq: "loveYourMuscles/love-your-muscles-bottle.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    cyclist: file(relativePath: { eq: "loveYourMuscles/cycling-uphill.jpeg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    curl: file(relativePath: { eq: "loveYourMuscles/woman-with-dumbell.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;
